import axios from "axios";
import react, { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";
import SafeArea from "components/safe-area";
import { TInvoice } from "types/invoice";
import Tab from "components/tab";
import useQueryString from "hooks/useQueryString";
import InvoiceTable from "containers/invoiceTable";
import { useTable } from "components/table";
import Field from "components/field";
import { useTranslation } from 'react-i18next';

type Status = {
  id: number;
  label: string;
  color: string;
};

/**
 * GET /api/v2/company/invoices/invoice-list
 * - page: 1-based; backend uses fixed 50 rows/page (limit ignored).
 * - total_pages and page_count: number of pages for current tab + search (+ customerId if any).
 * - count and total_rows: total matching rows (same filters).
 * - has_more: page < total_pages
 */
export type InvoiceListResponse = {
  invoices: TInvoice[];
  all_invoices_count: number;
  status_counts: { [key: string]: number };
  page_count: number;
  total_pages?: number;
  count: number;
  total_rows?: number;
  has_more?: boolean;
};

function invoiceListTotalPages(data: InvoiceListResponse | null | undefined): number {
  if (!data) return 1;
  const fromApi =
    typeof data.total_pages === "number" && Number.isFinite(data.total_pages)
      ? data.total_pages
      : typeof data.page_count === "number" && Number.isFinite(data.page_count)
      ? data.page_count
      : null;
  if (fromApi == null || fromApi < 1) return 1;
  return Math.max(1, Math.floor(fromApi));
}

export default function Invoices() {
  const { t } = useTranslation();
  const tBase = "views.invoices.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const history = useHistory(),
    queryString = useQueryString();

  const [search, setSearch] = useState("");

  const onSort = (e: any) => {
    queryString.update({ ...e, page: 1 });
    // setStoredSort(e)
  };

  const sort = {
    column:
      queryString.search.column && !Array.isArray(queryString.search.column)
        ? queryString.search.column
        : null,
    order:
      queryString.search.order && !Array.isArray(queryString.search.order)
        ? queryString.search.order
        : null,
  };

  const rawPage = queryString.search.page;
  const currentPage =
    rawPage != null &&
    !Array.isArray(rawPage) &&
    Number.isFinite(Number(rawPage)) &&
    Number(rawPage) >= 1
      ? Math.floor(Number(rawPage))
      : 1;

  const invoiceListParams = { ...queryString.search, page: currentPage };

  const invoices = useQuery<InvoiceListResponse>(
    ["invoices", invoiceListParams],
    async () =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v2/company/invoices/invoice-list`,
          {
            params: invoiceListParams,
          }
        )
        .then((res) => res.data),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  const totalInvoicePages = useMemo(
    () => invoiceListTotalPages(invoices.data),
    [invoices.data]
  );

  const table = useTable({
    url: true,
    onSort,
    sort,
    pageIndex: currentPage,
    total: totalInvoicePages,
    onPaginate: (page) => queryString.update({ ...queryString.search, page }),
  });

  function removeParentheses(str: any) {
    return parseInt(str.replace(/[^\d]/g, ""), 10);
  }

  const handleClick = (row: TInvoice) => {
    history.push(`/invoices/${row.id}`);
  };

  const tabData = {
    all: {
      tab: tr("All Invoices"),
      count: invoices.data?.all_invoices_count
        ? removeParentheses(invoices.data.all_invoices_count)
        : 0,
    },
    open: {
      tab: tr("Open"),
      count: invoices.data?.status_counts
        ? invoices.data?.status_counts["17"]
        : 0,
    },
    unpaid: {
      tab: tr("Unpaid"),
      count: invoices.data?.status_counts
        ? invoices.data?.status_counts["33"]
        : 0,
    },
    partially_paid: {
      tab: tr("Partially Paid"),
      count: invoices.data?.status_counts
        ? invoices.data?.status_counts["38"]
        : 0,
    },
    paid: {
      tab: tr("Paid"),
      count: invoices.data?.status_counts
        ? invoices.data?.status_counts["18"]
        : 0,
    },
    // pending: {
    //   tab: "Pending",
    //   count: invoices.data?.status_counts
    //     ? invoices.data?.status_counts["37"]
    //     : 0,
    // },
  };

  return (
    <div className="divide-y">
      <SafeArea>
        <div className="py-4">
          <h1>{tr("Invoices")}</h1>
          <Field
            // onChange={(e) => setSearch(e.target.value)}
            placeholder={tr("Search by Customer")}
            onKeyDown={(e: any) => {
              if (e.key === "Enter") {
                e.target.blur();
              }
            }}
            onBlur={(e) =>
              queryString.update({
                ...queryString.search,
                search: e.target.value,
                page: 1,
              })
            }
          />
        </div>
        <Tab
          defaultTab={
            queryString.search.tab && !Array.isArray(queryString.search.tab)
              ? queryString.search.tab
              : "all"
          }
          headerOnly
          data={tabData}
          onClick={(e) =>
            queryString.update({ ...queryString.search, tab: e, page: 1 })
          }
        />
      </SafeArea>
      <SafeArea>
        <div className="py-4">
          <InvoiceTable
            pageIndex={currentPage}
            data={invoices.data?.invoices}
            handleClick={handleClick}
            table={table}
            fixedColumnWidths
          />
        </div>
      </SafeArea>
    </div>
  );
}
